import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Loader2, Target } from "lucide-react";
import { auth, getToken, getStoredUser, shootoutApi, shareLink } from "../lib/api";
import { GuestLock } from "../components/GuestUnlock";
import { SpinPick } from "../components/penalty/SpinPick";
import { KickRound } from "../components/penalty/KickRound";
import { Scoreboard } from "../components/penalty/Scoreboard";
import { ShareLinkBox } from "../components/penalty/ShareLinkBox";
import { ShootoutResult } from "../components/penalty/ShootoutResult";
import WagerSignupModal from "../components/WagerSignupModal";
import AuthForm from "../components/AuthForm";
import { Toaster } from "../components/ui/toaster";
import * as sfx from "../lib/stadiumSound";
import { Volume2, VolumeX } from "lucide-react";

const errText = (e) => e?.response?.data?.detail || "Something went wrong";

export const PenaltyTopBar = ({ coins, onBack }) => (
  <div className="sticky top-0 z-40 bg-[#0F172A]/85 backdrop-blur-md border-b border-white/5">
    <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
      <button data-testid="penalty-back-btn" onClick={onBack} className="pill-nav rounded-full px-3.5 py-1.5 flex items-center gap-2 text-sm font-medium text-slate-200">
        <ArrowLeft className="w-4 h-4" /> Home
      </button>
      <div className="font-display font-extrabold text-base sm:text-lg flex items-center gap-1.5 tracking-tight">
        <Target className="w-4 h-4 text-[#10B981]" />
        <span className="hidden min-[420px]:inline text-white">PENALTY</span>
        <span className="hidden min-[420px]:inline text-[#38BDF8]">SHOOTOUT</span>
      </div>
      <div className="flex items-center gap-1.5 rounded-full bg-[#10B981]/12 border border-[#10B981]/30 px-3 py-1.5">
        <Coins className="w-4 h-4 text-[#10B981]" />
        <span data-testid="penalty-coins" className="font-display font-bold text-sm text-[#10B981]">{coins == null ? "—" : `£${coins.toLocaleString()}`}</span>
        <GuestLock />
      </div>
    </div>
  </div>
);

const SoundToggle = () => {
  const [muted, setM] = useState(sfx.isMuted());
  const toggle = () => {
    sfx.setMuted(!muted);
    setM(!muted);
    if (muted) sfx.startAmbience();
  };
  return (
    <button data-testid="sound-toggle" onClick={toggle} aria-label={muted ? "Unmute" : "Mute"} className="ml-auto flex items-center gap-1.5 text-xs text-slate-300 hover:text-white rounded-full border border-white/10 px-3 py-1.5">
      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-[#10B981]" />} {muted ? "Sound off" : "Sound on"}
    </button>
  );
};

const Waiting = ({ text }) => (
  <div data-testid="shootout-waiting" className="max-w-md mx-auto text-center py-16 text-slate-300 flex flex-col items-center gap-3">
    <Loader2 className="w-7 h-7 animate-spin text-[#10B981]" /> {text}
  </div>
);

function PenaltyMatchInner({ id }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [ready, setReady] = useState(false);
  const [s, setS] = useState(null);
  const [coins, setCoins] = useState(getStoredUser()?.coins ?? null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealedN, setRevealedN] = useState(null);
  const joined = useRef(false);
  const live = s?.status === "shooting";
  const done = s?.status === "complete";

  const wasLive = useRef(false);
  useEffect(() => {
    if (live) { wasLive.current = true; sfx.startAmbience(); }
    return () => sfx.stopAmbience();
  }, [live]);
  useEffect(() => {
    if (done && wasLive.current && revealedN !== null && revealedN >= (s?.rounds.filter((r) => r.resolved).length || 0)) {
      sfx.stopAmbience();
      sfx.whistle(3);
      wasLive.current = false;
    }
  }, [done, revealedN]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = useCallback((res) => {
    setS(res.shootout);
    if (res.coins != null) setCoins(res.coins);
    setRevealedN((v) => (v === null ? res.shootout.rounds.filter((r) => r.resolved).length : v));
  }, []);

  useEffect(() => {
    auth.ensure().then((u) => { setUser(u); if (u) { setCoins(u.coins); setReady(true); } }).catch(() => setError("Couldn't load your account"));
  }, []);

  useEffect(() => {
    if (!ready) return;
    let live = true;
    const tick = async () => {
      try {
        const res = await shootoutApi.get(id);
        if (!live) return;
        const sh = res.shootout;
        if (sh.status === "lobby" && !sh.me && !sh.opponent && !joined.current) {
          joined.current = true;
          apply(await shootoutApi.join(id));
          return;
        }
        apply(res);
      } catch (e) {
        if (e?.response?.status === 404) setError("This shootout link doesn't exist");
        else if (joined.current && e?.response?.status === 400) setError(errText(e));
      }
    };
    tick();
    const t = setInterval(tick, 1300);
    return () => { live = false; clearInterval(t); };
  }, [ready, id, apply]);

  const confirmSquad = async (taker, keeper) => {
    setBusy(true);
    try { apply(await shootoutApi.squad(id, taker, keeper)); } catch (e) { setError(errText(e)); }
    setBusy(false);
  };
  const submitKick = async (n, shot, dive) => {
    try { apply(await shootoutApi.kick(id, n, shot, dive)); } catch (e) { setError(errText(e)); }
  };
  const rematch = async () => {
    setBusy(true);
    try {
      const r = await shootoutApi.rematch(id);
      navigate(`/p/${r.rematch_id}`);
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  };
  const pepAgain = async () => {
    setBusy(true);
    try {
      const r = await shootoutApi.pep(s?.stake || 0);
      navigate(`/p/${r.shootout.id}`);
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  };
  const cancel = async () => {
    try { apply(await shootoutApi.cancel(id)); navigate("/penalties"); } catch (e) { setError(errText(e)); }
  };

  const body = () => {
    if (!ready && user === null)
      return (
        <div className="max-w-md mx-auto">
          <AuthForm
            onAuthed={(u) => { setUser(u); setCoins(u.coins); setReady(true); }}
            title="You've been challenged to penalties!"
            subtitle="Sign up free (£30 virtual to start) and you'll go straight to the spin."
          />
        </div>
      );
    if (error) return <div data-testid="shootout-error" className="max-w-md mx-auto text-center py-16 text-[#EF4444] font-semibold">{error}</div>;
    if (!s) return <Waiting text="Loading shootout…" />;
    if (s.status === "cancelled") return <Waiting text="This shootout was cancelled." />;
    if (!s.me) return <Waiting text="Joining…" />;
    const me = { ...s[s.me], role: s.me };
    const themRole = s.me === "creator" ? "opponent" : "creator";
    if (s.status === "lobby" && s.invited && s.challenge)
      return (
        <div className="max-w-md mx-auto">
          <p data-testid="challenge-sent-note" className="text-center text-sm text-[#38BDF8] mb-3">Challenge sent to {s.invited_name}. They can accept from Home or their alerts.</p>
          <ShareLinkBox link={shareLink("p", s.id)} stake={s.stake} onCancel={cancel} />
        </div>
      );
    if (s.status === "lobby" && s.invited)
      return (
        <div data-testid="rematch-waiting" className="card-38 p-6 hover:translate-y-0 max-w-md mx-auto text-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#10B981] mx-auto" />
          <h2 className="font-display font-extrabold text-2xl text-white mt-3">Rematch sent</h2>
          <p className="text-slate-400 text-sm mt-1">Waiting for {s.invited_name} to accept{s.stake ? ` · £${s.stake} each` : ""}…</p>
          <button data-testid="rematch-cancel-btn" onClick={cancel} className="mt-4 text-xs text-slate-500 hover:text-white underline">Cancel rematch</button>
        </div>
      );
    if (s.status === "lobby") return <ShareLinkBox link={shareLink("p", s.id)} stake={s.stake} onCancel={cancel} />;
    const them = { ...s[themRole], role: themRole };
    if (s.status === "picking") {
      if (me.taker) return <Waiting text={`Squad locked. Waiting for ${them.username} to sign theirs…`} />;
      return <SpinPick onConfirm={confirmSquad} busy={busy} />;
    }
    const revealed = s.rounds.filter((r) => r.resolved && r.n <= revealedN).map((r) => r.resolved);
    const current = s.rounds.find((r) => r.n === revealedN + 1);
    const finished = s.status === "complete" && !current;
    return (
      <div className="max-w-md mx-auto space-y-3">
        <div className="flex"><SoundToggle /></div>
        <Scoreboard me={me} them={them} revealed={revealed} />
        {finished ? (
            <ShootoutResult
              s={s}
              me={me}
              them={them}
              busy={busy}
              onRematch={rematch}
              onPepAgain={pepAgain}
              onAccept={() => navigate(`/p/${s.rematch_id}`)}
              onNew={() => navigate("/penalties")}
              onHome={() => navigate("/")}
            />
        ) : current ? (
          <KickRound
            key={current.n}
            round={current}
            me={me}
            them={them}
            myIn={current[`${s.me}_in`]}
            onSubmit={submitKick}
            onRevealed={(n) => setRevealedN(n)}
          />
        ) : (
          <Waiting text="Next kick loading…" />
        )}
        {s.pep && !finished && s.stake > 0 && (
          <div className="text-center text-xs text-slate-400" data-testid="shootout-pep-reward">{`£${s.stake} staked · beat Pep to win £${s.stake * 2}`}</div>
        )}
        {s.pot > 0 && !finished && !s.pep && (
          <div className="text-center text-xs text-slate-400" data-testid="shootout-pot">Winner takes the £{s.pot} pot</div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="penalty-match-page">
      <PenaltyTopBar coins={coins} onBack={() => navigate("/")} />
      <div className="px-4 py-5">{body()}</div>
      <Toaster />
    </div>
  );
}

export default function PenaltyMatch() {
  const { id } = useParams();
  return <PenaltyMatchInner key={id} id={id} />;
}
